import { useCallback, useEffect, useRef, useState } from 'react';

/** Delay before clearing readOnly so Chrome/Safari cannot attach form-history UI on focus. */
const AUTOFILL_UNLOCK_MS = 100;

/**
 * Shared browser autofill opt-out for combobox / free-text fields.
 * Chrome ignores `autocomplete="off"`; `new-password` + deferred readOnly is more reliable.
 * Do not unlock on mousedown — browsers often attach history UI then.
 */
export function useBlockBrowserAutofill(disabled = false) {
    const [blockBrowserAutofill, setBlockBrowserAutofill] = useState(true);
    const nonceRef = useRef(`no-autofill-${Math.random().toString(36).slice(2, 11)}`);
    const unlockTimerRef = useRef<number | null>(null);

    const clearUnlockTimer = useCallback(() => {
        if (unlockTimerRef.current != null) {
            window.clearTimeout(unlockTimerRef.current);
            unlockTimerRef.current = null;
        }
    }, []);

    useEffect(() => () => clearUnlockTimer(), [clearUnlockTimer]);

    const unlockAutofillGuard = useCallback(() => {
        clearUnlockTimer();
        unlockTimerRef.current = window.setTimeout(() => {
            setBlockBrowserAutofill(false);
            unlockTimerRef.current = null;
        }, AUTOFILL_UNLOCK_MS);
    }, [clearUnlockTimer]);

    const autofillInputProps = {
        // Random name each mount so Chrome cannot match prior form history by field name.
        name: nonceRef.current,
        autoComplete: 'new-password' as const,
        autoCorrect: 'off' as const,
        autoCapitalize: 'off' as const,
        spellCheck: false as const,
        readOnly: disabled || blockBrowserAutofill,
        'data-lpignore': 'true',
        'data-1p-ignore': 'true',
        'data-bwignore': 'true',
        'data-form-type': 'other',
    };

    return {
        blockBrowserAutofill,
        unlockAutofillGuard,
        autofillInputProps,
    };
}
